import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query";

import { captureHandledError } from "./glitchtip";

function isBrowserRuntime() {
  return typeof window !== "undefined";
}

function serializeQueryKey(queryKey: unknown) {
  try {
    return JSON.stringify(queryKey);
  } catch {
    return String(queryKey);
  }
}

export function createQueryClient() {
  return new QueryClient({
    queryCache: new QueryCache({
      onError(error, query) {
        if (!isBrowserRuntime()) {
          return;
        }

        void captureHandledError({
          error,
          options: {
            tags: {
              area: "query",
              surface: "browser",
            },
            contexts: {
              query: {
                queryHash: query.queryHash,
                queryKey: serializeQueryKey(query.queryKey),
              },
            },
          },
        });
      },
    }),
    mutationCache: new MutationCache({
      onError(error, variables, _context, mutation) {
        if (!isBrowserRuntime()) {
          return;
        }

        void captureHandledError({
          error,
          options: {
            tags: {
              area: "mutation",
              surface: "browser",
            },
            contexts: {
              mutation: {
                mutationKey: serializeQueryKey(mutation.options.mutationKey ?? []),
                variables,
              },
            },
          },
        });
      },
    }),
    defaultOptions: {
      queries: {
        staleTime: 15_000,
        gcTime: 5 * 60_000,
        retry: 1,
        refetchOnWindowFocus: false,
      },
      mutations: {
        retry: 0,
      },
    },
  });
}

export type AppQueryClient = ReturnType<typeof createQueryClient>;
